import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { bbox } from '@turf/turf';

import getFeatureByEastingNorthings from 'client/utils/coords/coordUtils';
import OSGridReference from 'client/utils/coords/osGridRef';
import SearchService from 'client/services/Search/Search.service';
import { POINT } from 'client/utils/constants/geometryType';
import { MapContext } from 'os-elements';
import { getEpsgCode } from 'client/utils/helpers/MapConfig/MapConfig';
import { useLocation } from 'react-router';

const minChar = 1;
const maxChars = 50;
const NONE_HIGHLIGHTED = -1;
// https://github.com/stemount/gov-uk-official-postcode-regex-helper
const POSTCODE_REGEX = /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) [0-9][A-Za-z]{2})$/;

export const STATUSES = {
    IDLE: 'idle',
    SEARCHING: 'searching',
    ERROR: 'error',
    NO_RESULTS: 'no-results'
};

export const PAN_STATUSES = {
    IDLE: 'idle',
    PANNING: 'panning',
    COMPLETE: 'complete'
};

export default function useSearchController () {
    const intl = useIntl();
    const { map } = useContext(MapContext);
    const location = useLocation();
    const searchBtnRef = useRef(null);
    const inputRef = useRef(null);
    const resultsRef = useRef(null);
    const debounceTimeoutRef = useRef(null);
    const [search, setSearch] = useState('');
    const [errorMessage, setErrorMessage] = useState(null);
    const [results, setResults] = useState(null);
    const [status, setStatus] = useState(STATUSES.IDLE);
    const [resultsHidden, setResultsHidden] = useState(true);
    const [highlightedIndex, setHighlightedIndex] = useState(NONE_HIGHLIGHTED);
    const [panStatus, setPanStatus] = useState(PAN_STATUSES.IDLE);

    const preventSearch = useRef(false);

    const open = Boolean(errorMessage || results || status === STATUSES.SEARCHING);
    const disabled = Boolean(status === STATUSES.SEARCHING);

    const showError = useCallback((message) => {
        setErrorMessage(message);
        setStatus(STATUSES.ERROR);
        setResultsHidden(false);
    }, []);

    const getCharLengthError = useCallback(() => {
        return intl.formatMessage({ id: 'search.error.charLength' }) + minChar + ' & ' + maxChars;
    }, [intl]);

    const validateSearchTerm = useCallback((value) => {
        const isValid = value.length <= maxChars;
        if (!value || isValid) {
            setErrorMessage(null);
            return true;
        }
        showError(getCharLengthError());
        return false;
    }, [getCharLengthError, showError]);

    const selectFeature = (feature) => {
        const view = map?.getView();
        if (view) {
            setPanStatus(PAN_STATUSES.PANNING);
            const onPanComplete = () => setPanStatus(PAN_STATUSES.COMPLETE);
            if (feature?.geometry?.type === POINT) {
                view.animate({
                    zoom: 19,
                    center: feature?.geometry?.coordinates
                }, onPanComplete);
            } else {
                view.fit(bbox(feature), { callback: onPanComplete });
            }
        }
        const featureText = feature?.properties?.displayText.split(',') || [];
        const postcode = featureText.filter(text => POSTCODE_REGEX.test(text))?.[0];
        if (postcode) {
            preventSearch.current = true;
            setSearch(postcode);
        }
        setResultsHidden(true);
    };

    useEffect(() => {
        if (status === STATUSES.ERROR) {
            setResults(null);
        }
    }, [status]);

    useEffect(() => {
        // Handles search term passed from workspace tile
        if (location.state?.searchTerm && location.state?.searchTerm !== search && inputRef?.current) {
            setSearch(location.state.searchTerm);
            inputRef?.current.focus();
        }
    }, [location.state?.searchTerm, inputRef?.current]);

    useEffect(() => {
        if (preventSearch.current) {
            preventSearch.current = false;
            return;
        }
        if (!validateSearchTerm(search)) {
            return;
        }
        // Calls search API whenever the search term is updated in state
        setHighlightedIndex(NONE_HIGHLIGHTED);
        if (search.length <= 0) {
            clearResults();
        } else {
            const delayLength = search === location?.state?.searchTerm ? 0 : 600;
            debounceTimeoutRef.current = setTimeout(handleSearch, delayLength);
            return () => {
                if (debounceTimeoutRef.current) {
                    clearTimeout(debounceTimeoutRef.current);
                    debounceTimeoutRef.current = null;
                }
            };
        }
    }, [location?.state?.searchTerm, search, validateSearchTerm]);

    const handleSearch = async (value) => {
        const searchTerm = value || search;
        if (!searchTerm) {
            clearResults();
            setStatus(STATUSES.IDLE);
            return;
        }
        if (!validateSearchTerm(searchTerm)) {
            return;
        }
        setStatus(STATUSES.SEARCHING);
        const features = [];
        const eastNorthFeature = getFeatureByEastingNorthings(searchTerm);
        if (eastNorthFeature) {
            features.push(eastNorthFeature);
        }
        if (OSGridReference.isValid(searchTerm.toUpperCase())) {
            const gridRef = new OSGridReference(searchTerm.toUpperCase());
            if (gridRef.quadrant) {
                features.push(gridRef.getGridSquareGeoJson());
            } else {
                features.push(gridRef.toGeoJson());
            }
        }
        if (searchTerm) {
            try {
                const epsg = await getEpsgCode();
                const searchFeatures = await SearchService.getSearchAsync(searchTerm, epsg);
                if (searchFeatures) {
                    features.push(...searchFeatures.features);
                }
            } catch (err) {
                showError(intl.formatMessage({ id: 'search.error.unavailable' }));
                return;
            }
        }
        setResults(features);
        setResultsHidden(false);
        setStatus(features?.length > 0 ? STATUSES.IDLE : STATUSES.NO_RESULTS);
    };
    const clearResults = () => {
        setResults(null);
        setHighlightedIndex(NONE_HIGHLIGHTED);
    };
    const handleSearchKeypress = async evt => {
        const { keyCode, key } = evt;
        if (keyCode === 27 || key === 'Escape') {
            clearResults();
        }
        if (keyCode === 13 || key === 'Enter') {
            if (highlightedIndex >= 0) {
                selectFeature(results[highlightedIndex]);
            } else {
                await handleSearch();
            }
        }
        if ((evt.key === 'ArrowDown' || evt.key === 'ArrowUp') && results?.length > 0) {
            if (evt.key === 'ArrowDown' && highlightedIndex < results?.length - 1) {
                const nextIndex = highlightedIndex >= 0 ? highlightedIndex + 1 : 0;
                setHighlightedIndex(nextIndex);
            } else if (evt.key === 'ArrowUp') {
                const nextIndex = highlightedIndex > 0 ? highlightedIndex - 1 : NONE_HIGHLIGHTED;
                setHighlightedIndex(nextIndex);
            }
            evt.preventDefault();
        }
    };

    const handleResultsFocus = useCallback(() => {
        if (highlightedIndex === NONE_HIGHLIGHTED && results?.length > 0) {
            setHighlightedIndex(0);
        }
    }, [highlightedIndex, results?.length]);

    const handleOnChange = useCallback((evt) => {
        setSearch(evt?.target?.value);
    }, []);

    const handleInputFocus = useCallback(() => {
        setResultsHidden(false);
    }, []);

    const handleSearchBtnClick = useCallback(async () => {
        if (disabled) {
            return;
        }
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
            debounceTimeoutRef.current = null;
        }
        setResultsHidden(false);
        searchBtnRef.current?.blur?.();
        await handleSearch();
    }, [disabled, handleSearch]);

    const hideSearch = useCallback(() => {
        setResultsHidden(true);
    }, []);

    return {
        refs: { searchBtnRef, inputRef, resultsRef },
        state: { searchTerm: search, errorMessage, results, status, resultsHidden, highlightedIndex, panStatus },
        derived: { open, disabled },
        actions: {
            hideSearch,
            handleOnChange,
            handleInputFocus,
            handleSearchKeypress,
            handleResultsFocus,
            handleSearchBtnClick
        },
        selectFeature
    };
}
